/**
 * Schemas Zod reutilizáveis (primitivos, identificadores, datas).
 * Bases pra schemas mais ricos de entidade.
 */

import { z } from 'zod';

/** UUID v4 */
export const uuidSchema = z.string().uuid();

/** ISO 8601 timestamp (ex: 2026-05-17T10:30:00.000Z) */
export const isoTimestampSchema = z.string().datetime({ offset: true });

/** E-mail (1 endereço único) */
export const emailSchema = z.string().email().toLowerCase().trim();

/**
 * Lista de e-mails separados por vírgula, ponto-e-vírgula ou quebra de linha.
 *
 * Aceita "a@b.com, c@d.com" / "a@b.com; c@d.com" / "a@b.com\nc@d.com".
 * Retorna string canônica deduplicada e em lowercase ("a@b.com, c@d.com").
 *
 * Retrocompat: 1 e-mail único continua válido (entra e sai igual ao
 * emailSchema). UI mostra hint pro usuário separar múltiplos com vírgula.
 *
 * Use `parseEmailList(s)` pra obter o array de e-mails individuais.
 */
export const emailListSchema = z
  .string()
  .trim()
  .min(1)
  .transform((s) => parseEmailList(s))
  .refine((arr) => arr.length > 0, 'Informe ao menos 1 e-mail válido')
  .refine(
    (arr) => arr.every((e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)),
    'Todos os e-mails precisam ter formato válido (ex: nome@dominio.com)',
  )
  .transform((arr) => arr.join(', '));

/**
 * Quebra uma string com múltiplos e-mails em array deduplicado.
 * Separadores: vírgula, ponto-e-vírgula, quebra de linha, espaço.
 */
export function parseEmailList(raw: string): string[] {
  return Array.from(
    new Set(
      raw
        .split(/[,;\n\s]+/)
        .map((s) => s.trim().toLowerCase())
        .filter((s) => s.length > 0),
    ),
  );
}

/** CNPJ (apenas dígitos, 14 chars) */
export const cnpjSchema = z.string().regex(/^\d{14}$/, 'CNPJ deve ter 14 dígitos numéricos');

/** CPF (apenas dígitos, 11 chars) */
export const cpfSchema = z.string().regex(/^\d{11}$/, 'CPF deve ter 11 dígitos numéricos');

/** Telefone brasileiro (10 ou 11 dígitos com DDD) */
export const telefoneBrSchema = z
  .string()
  .regex(/^\d{10,11}$/, 'Telefone deve ter 10 ou 11 dígitos (DDD + número)');

/** Valor monetário em centavos (inteiro não-negativo) */
export const valorCentavosSchema = z.number().int().nonnegative();

/** Quantidade (inteiro positivo) */
export const quantidadeSchema = z.number().int().positive();

/** Texto curto (1-200 chars, sem newlines) */
export const textoCurtoSchema = z
  .string()
  .min(1)
  .max(200)
  .transform((s) => s.trim());

/** Texto longo (1-2000 chars, multi-linha permitido) */
export const textoLongoSchema = z
  .string()
  .min(1)
  .max(2000)
  .transform((s) => s.trim());
